import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { authFetch } from '../utils/api';
import Skeleton from './common/Skeleton';
import EmptyState from './common/EmptyState';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';

const BASE_URL = config.BASE_URL;

function statusBadge(status) {
  const styles = {
    open:      { background: '#2563eb22', color: '#2563eb', border: '1px solid #2563eb44' },
    confirmed: { background: '#dc262622', color: '#dc2626', border: '1px solid #dc262644' },
    dismissed: { background: '#6b728022', color: '#6b7280', border: '1px solid #6b728044' },
  };
  const s = styles[status] || styles.open;
  return (
    <span style={{ ...s, borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
      {status || 'open'}
    </span>
  );
}

function detectionMethodBadge(method) {
  const map = {
    'jaro_winkler':        { icon: '📐', label: 'Jaro-Winkler',   color: '#7c3aed' },
    'damerau_levenshtein': { icon: '📏', label: 'Edit Distance',   color: '#7c3aed' },
    'combosquat':          { icon: '🔗', label: 'Combosquat',      color: '#b45309' },
    'prefix_suffix':       { icon: '✂️', label: 'Prefix/Suffix',   color: '#0369a1' },
    'unicode':             { icon: '🌐', label: 'Unicode',          color: '#b91c1c' },
    'osv':                 { icon: '🔴', label: 'OSV MAL-',         color: '#dc2626' },
    'similarity':          { icon: '🎯', label: 'Similarity',      color: '#7c3aed' },
  };
  const m = map[method] || map['jaro_winkler'];
  return (
    <span style={{ background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}44`, borderRadius: '4px', padding: '2px 7px', fontSize: '0.7rem', fontWeight: 600 }}>
      {m.icon} {m.label}
    </span>
  );
}

function confidenceBadge(confidence) {
  const isHigh = confidence === 'HIGH';
  return confidence ? (
    <span style={{ background: isHigh ? '#dc262618' : '#d9770618', color: isHigh ? '#dc2626' : '#d97706', border: `1px solid ${isHigh ? '#dc262644' : '#d9770644'}`, borderRadius: '4px', padding: '2px 7px', fontSize: '0.7rem', fontWeight: 600 }}>
      {isHigh ? '🔴 HIGH' : '🟡 MEDIUM'}
    </span>
  ) : null;
}

function FindingRow({ finding, acting, onAction }) {
  const id = finding.id;
  const isMalware = (finding.type || finding.findingType) === 'malware';
  const status = finding.status || 'open';
  const isOpen = status === 'open';
  const isConfirmedMalware = isMalware && status === 'confirmed';
  const similarityRaw = finding.similarityScore ?? finding.similarity_score;
  const similarityPct = similarityRaw != null
    ? (similarityRaw > 1 ? Math.round(similarityRaw) : Math.round(similarityRaw * 100))
    : null;

  return (
    <div style={{
      border: `1px solid ${isMalware ? '#dc262644' : '#d9770644'}`,
      borderRadius: '8px',
      padding: '0.875rem 1rem',
      marginBottom: '0.625rem',
      background: isMalware ? 'rgba(220,38,38,0.03)' : 'rgba(217,119,6,0.03)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          {/* Package name + badges */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.375rem' }}>
            <code style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.875rem' }}>
              {finding.packageName || finding.package_name || finding.name}
              {finding.version && (
                <span style={{ color: 'var(--bs-secondary-color)', fontWeight: 400 }}>@{finding.version}</span>
              )}
            </code>
            {finding.ecosystem && (
              <span style={{ background: 'var(--bs-tertiary-bg)', color: 'var(--bs-secondary-color)', border: '1px solid var(--bs-border-color)', borderRadius: '4px', padding: '2px 6px', fontSize: '0.7rem' }}>
                {finding.ecosystem}
              </span>
            )}
            {isMalware
              ? <span style={{ background: '#dc262622', color: '#dc2626', border: '1px solid #dc262644', borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 700 }}>🦠 MALWARE</span>
              : <span style={{ background: '#d9770622', color: '#d97706', border: '1px solid #d9770644', borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 700 }}>⚠️ TYPOSQUAT</span>
            }
            {finding.detectionMethod && detectionMethodBadge(finding.detectionMethod)}
            {finding.confidence && confidenceBadge(finding.confidence)}
            {statusBadge(status)}
          </div>

          {/* Malware: MAL- ID + rich description */}
          {isMalware && (
            <div style={{ marginTop: '0.25rem' }}>
              {(finding.malId || finding.malwareId) && (
                <div style={{ fontSize: '0.8125rem', marginBottom: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <code style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#dc2626', background: '#dc262615', padding: '1px 6px', borderRadius: '4px' }}>
                    {finding.malId || finding.malwareId}
                  </code>
                  <span style={{ color: 'var(--bs-secondary-color)', fontSize: '0.75rem' }}>Confirmed malware record (OSV database)</span>
                  <a href={`https://osv.dev/vulnerability/${finding.malId || finding.malwareId}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: '#2563eb' }}>
                    View OSV advisory →
                  </a>
                </div>
              )}
              {finding.description && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--bs-body-color)', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                  {finding.description}
                </div>
              )}
              <div style={{
                fontSize: '0.8rem', lineHeight: 1.6,
                padding: '0.375rem 0.625rem',
                borderRadius: '6px',
                background: 'rgba(220,38,38,0.06)',
                border: '1px solid rgba(220,38,38,0.2)',
                color: 'var(--bs-body-color)',
              }}>
                <strong>⚠️ What to do:</strong> This package is confirmed malicious and has <strong>no safe version</strong>.
                Remove it from your project immediately and audit your dependency tree for any other packages installed by it.
                Check your CI/CD logs for signs of credential theft or data exfiltration.
              </div>
            </div>
          )}

          {/* Typosquat: rich context block */}
          {!isMalware && (finding.similarTo || finding.similar_to) && (() => {
            const legitPkg   = finding.similarTo || finding.similar_to;
            const suspectPkg = finding.packageName || finding.package_name || finding.pkgName;
            return (
              <div style={{ marginTop: '0.25rem' }}>

                {/* What was detected */}
                <div style={{ fontSize: '0.8125rem', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                  <span style={{ color: 'var(--bs-secondary-color)' }}>This package name is </span>
                  <strong style={{ color: '#d97706' }}>{similarityPct}% similar</strong>
                  <span style={{ color: 'var(--bs-secondary-color)' }}> to the popular and legitimate package </span>
                  <code style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--bs-body-color)' }}>{legitPkg}</code>
                  <span style={{ color: 'var(--bs-secondary-color)' }}>. This is a common typosquatting pattern where attackers publish a malicious package under a misspelled name hoping developers install it by mistake.</span>
                </div>

                {/* Side-by-side name comparison */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  background: 'var(--bs-tertiary-bg)', borderRadius: '6px',
                  padding: '0.5rem 0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap',
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#d97706', marginBottom: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>⚠️ Found in SBOM</div>
                    <code style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#d97706', fontSize: '0.875rem' }}>{suspectPkg}</code>
                  </div>
                  <div style={{ color: 'var(--bs-secondary-color)', fontSize: '1.1rem' }}>→</div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#16a34a', marginBottom: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>✓ Legitimate package</div>
                    <code style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#16a34a', fontSize: '0.875rem' }}>{legitPkg}</code>
                  </div>
                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--bs-secondary-color)', marginBottom: '0.2rem' }}>Name similarity</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: 80, height: 6, borderRadius: 3, background: 'var(--bs-border-color)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${similarityPct}%`, background: similarityPct >= 95 ? '#dc2626' : '#d97706', borderRadius: 3 }} />
                      </div>
                      <span style={{ fontWeight: 700, fontSize: '0.8125rem', color: similarityPct >= 95 ? '#dc2626' : '#d97706' }}>{similarityPct}%</span>
                    </div>
                  </div>
                </div>

                {/* What to do */}
                <div style={{
                  fontSize: '0.8rem', lineHeight: 1.6,
                  padding: '0.375rem 0.625rem',
                  borderRadius: '6px',
                  background: 'rgba(217,119,6,0.06)',
                  border: '1px solid rgba(217,119,6,0.2)',
                  color: 'var(--bs-body-color)',
                }}>
                  <strong>What to do:</strong> Verify you intended to install <code style={{ fontFamily: 'var(--font-mono)' }}>{suspectPkg}</code>.
                  If you meant <code style={{ fontFamily: 'var(--font-mono)' }}>{legitPkg}</code>, remove the suspect package and replace it with the correct one.
                  Click <strong>Confirm</strong> to escalate this to a Critical finding and trigger alerts, or <strong>Dismiss</strong> if this is a false positive (e.g. an internal package with a similar name).
                </div>
              </div>
            );
          })()}

          {/* Confirmed typosquat escalation warning */}
          {!isMalware && status === 'confirmed' && (
            <div style={{
              marginTop: '0.5rem', padding: '0.375rem 0.75rem', borderRadius: '6px',
              background: '#dc262615', border: '1px solid #dc262633',
              fontSize: '0.8125rem', fontWeight: 600, color: '#dc2626',
            }}>
              ⚠️ Confirmed typosquat — treat as Critical. Remove <code style={{ fontFamily: 'var(--font-mono)', color: '#dc2626' }}>{finding.packageName || finding.pkgName}</code> and replace with <code style={{ fontFamily: 'var(--font-mono)', color: '#dc2626' }}>{finding.similarTo || finding.similar_to}</code>
            </div>
          )}
        </div>

        {/* Action buttons — only for open findings that aren't confirmed malware */}
        {isOpen && !isConfirmedMalware && (
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, alignSelf: 'flex-start' }}>
            {!isMalware && (
              <button
                className="ce-btn ce-btn--danger ce-btn--sm"
                disabled={acting === id}
                onClick={() => onAction(id, 'confirm')}
              >
                {acting === id ? '⏳' : '✓ Confirm'}
              </button>
            )}
            <button
              className="ce-btn ce-btn--secondary ce-btn--sm"
              disabled={acting === id}
              onClick={() => onAction(id, 'dismiss')}
            >
              {acting === id ? '⏳' : '✗ Dismiss'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MalwareView({ repoName, commitHash }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null); // id being actioned

  const filter = searchParams.get('mal_filter') || 'all';
  const page = Math.max(1, parseInt(searchParams.get('mal_page') || '1', 10));
  const pageSize = parseInt(localStorage.getItem('depguard_page_size') || String(DEFAULT_PAGE_SIZE), 10);

  const setFilter = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('mal_filter', val); n.set('mal_page', '1'); return n; });
  const setPage   = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('mal_page', String(val)); return n; });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await authFetch(`${BASE_URL}/sbom/${repoName}/${commitHash}/malware`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [repoName, commitHash]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    setActing(id);
    try {
      await authFetch(`${BASE_URL}/sbom/${repoName}/${commitHash}/malware/${id}/${action}`, { method: 'PUT' });
      await load();
    } catch (e) { console.error(e); }
    finally { setActing(null); }
  };

  if (loading) return (
    <div>
      <Skeleton type="cards" count={4} />
      <Skeleton type="text" count={4} />
    </div>
  );
  if (error) return <div className="alert alert-danger">{t('malware.loadError', 'Failed to load malware data')}: {error}</div>;

  const findings = Array.isArray(data) ? data : (data?.findings || []);
  const malwareFindings = findings.filter(f => (f.type || f.findingType) === 'malware');
  const typosquatFindings = findings.filter(f => (f.type || f.findingType) === 'typosquat');
  const openFindings = findings.filter(f => !f.status || f.status === 'open');
  const confirmedMalware = malwareFindings.filter(f => f.status === 'confirmed');

  const exportCsv = () => {
    const rows = [
      ['Package', 'Version', 'Type', 'Severity', 'Detection Method', 'Confidence', 'Ecosystem', 'Similar To', 'Score', 'MAL ID', 'Status', 'Description'],
      ...findings.map(f => [
        f.pkgName || f.packageName || f.name || '',
        f.pkgVersion || f.version || '',
        f.findingType || f.type || '',
        f.severity || '',
        f.detectionMethod || '',
        f.confidence || '',
        f.ecosystem || '',
        f.similarTo || f.similar_to || '',
        f.similarityScore != null ? (f.similarityScore > 1 ? f.similarityScore : Math.round(f.similarityScore * 100)) : '',
        f.malwareId || f.malId || '',
        f.status || '',
        (f.description || '').replace(/,/g, ';'),
      ])
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `malware-findings-${repoName}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!findings.length) {
    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={load}>🔄 {t('malware.rescan', 'Re-scan')}</button>
        </div>
        <EmptyState
          icon="🛡️"
          title={t('malware.emptyTitle', 'No malware findings')}
          description={t('malware.emptyDesc', 'No malware or typosquatting packages detected in this SBOM.')}
        />
      </div>
    );
  }

  const filtered = findings.filter(f => {
    if (filter === 'malware')   return (f.type || f.findingType) === 'malware';
    if (filter === 'typosquat') return (f.type || f.findingType) === 'typosquat';
    if (filter === 'open')      return !f.status || f.status === 'open';
    return true;
  });

  const nPages = Math.ceil(filtered.length / pageSize);
  const pagedFiltered = filtered.slice((page - 1) * pageSize, page * pageSize);

  const filteredMalware    = pagedFiltered.filter(f => (f.type || f.findingType) === 'malware');
  const filteredTyposquats = pagedFiltered.filter(f => (f.type || f.findingType) === 'typosquat');

  return (
    <div>
      {/* Summary bar */}
      <div className="ce-kpi-row mb-3">
        {[
          { label: t('malware.totalFindings', 'Total Findings'), value: findings.length,          icon: '🔍' },
          { label: t('malware.confirmed',     'Confirmed Malware'), value: confirmedMalware.length, icon: '🦠', danger: true },
          { label: t('malware.typosquats',    'Typosquats'),        value: typosquatFindings.length, icon: '⚠️', warn: true },
          { label: t('malware.open',          'Open'),              value: openFindings.length,    icon: '🔓' },
        ].map(s => (
          <div key={s.label} className="ce-kpi" style={s.danger && s.value > 0 ? { '--kpi-color': '#dc2626' } : s.warn && s.value > 0 ? { '--kpi-color': '#ca8a04' } : {}}>
            <div className="ce-kpi-value">{s.icon} {s.value}</div>
            <div className="ce-kpi-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter + Re-scan bar */}
      <div className="ce-filter-bar mb-3">
        {[
          { id: 'all',       label: t('malware.filterAll',       'All') },
          { id: 'malware',   label: `🦠 ${t('malware.filterMalware',   'Malware')}` },
          { id: 'typosquat', label: `⚠️ ${t('malware.filterTyposquat', 'Typosquats')}` },
          { id: 'open',      label: `🔓 ${t('malware.filterOpen',      'Open only')}` },
        ].map(f => (
          <button
            key={f.id}
            className={`ce-btn ce-btn--sm ${filter === f.id ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={load}>
          🔄 {t('malware.rescan', 'Re-scan')}
        </button>
        <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={exportCsv} disabled={!findings.length}>
          📥 {t('malware.exportCsv', 'Export CSV')}
        </button>
      </div>

      {/* Confirmed Malware section */}
      {filteredMalware.length > 0 && (
        <div className="mb-4">
          <div className="ce-section-header mb-2">
            <h6 className="ce-section-title">🦠 {t('malware.sectionMalware', 'Confirmed Malware (MAL-)')}</h6>
            <span className="ce-sev ce-sev--critical">{filteredMalware.length}</span>
          </div>
          {filteredMalware.map(finding => (
            <FindingRow key={finding.id} finding={finding} acting={acting} onAction={handleAction} />
          ))}
        </div>
      )}

      {/* Potential Typosquats section */}
      {filteredTyposquats.length > 0 && (
        <div className="mb-4">
          <div className="ce-section-header mb-2">
            <h6 className="ce-section-title">⚠️ {t('malware.sectionTyposquat', 'Potential Typosquats')}</h6>
            <span className="ce-sev ce-sev--high">{filteredTyposquats.length}</span>
          </div>
          {filteredTyposquats.map(finding => (
            <FindingRow key={finding.id} finding={finding} acting={acting} onAction={handleAction} />
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="ce-blank">
          <div className="ce-blank-icon">🔍</div>
          <div className="ce-blank-title">No findings match the current filter.</div>
        </div>
      )}

      {nPages > 1 && (
        <Pagination
          nPages={nPages}
          currentPage={page}
          setCurrentPage={setPage}
          pageSize={pageSize}
          totalItems={filtered.length}
        />
      )}
    </div>
  );
}
